import React, { FC, useCallback, useEffect } from 'react';

import WarningOutlined from '@ant-design/icons/WarningOutlined';
import { App, Button } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { ABOUT_ROUTE, DASHBOARD_ROUTE, FAQ_ROUTE, PRICING_ROUTE } from 'routes/dashboard/list';
import { CREATE_PROPERTY_FORM_ROUTE } from 'routes/property/list';
import { LANDING_ROUTE, PROFILE_ROUTE } from 'routes/user-management/list';
import 'tools/hooks/debounce.hook';
import { resolveImageUrl } from 'tools/imageUrl';
import { Icon, Typography } from 'ui';

import { MY_SERVICES_ROUTE } from 'routes/services/list';
import { watchedPropertiesSelector } from 'selectors/propertySlice.selector';
import { serviceViewTypeSelector } from 'selectors/systemSlice.selector';
// import { propertyListSelector } from 'selectors/propertySlice.selector'; // propertyList was removed from slice
import { useGetDevelopmentListMutation } from 'services/promoter/promoterApiService';
import { useGetWatchingPropertyQuery } from 'services/properties/propertyApiService';
// Changed to useLazyGetPropertiesQuery
import { useLazyGetCurrentUserQuery } from 'services/user-management/userManagementApiService';
import {
  setActiveProperty,
  setDevelopmentList,
  setInitialView,
  setWatchedProperties, // setPropertyList, // setPropertyList was removed from slice
} from 'slices/propertySlice';
import { setProfile } from 'slices/userSlice';
import { useAppDispatch, useAppSelector } from 'store/index';

// useAppSelector removed
import { Development } from 'types/promoters/promoterTypes';

// import { GetPropertyType } from 'types/property/propertyTypes';
// GetPropertyType removed
import s from './NavBar.module.scss';
import Filters from './filters';

interface NavBarProps {
  initiallyLogged: boolean;
}

const NavBar: FC<NavBarProps> = ({ initiallyLogged }) => {
  const { notification } = App.useApp();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const serviceViewType = useAppSelector(serviceViewTypeSelector);
  const [getDevelopmentList] = useGetDevelopmentListMutation();
  // const properties = useAppSelector(propertyListSelector); // propertyList was removed from slice

  const watchedProperties = useAppSelector(watchedPropertiesSelector);
  const location = useLocation();

  const {
    data: watchedPropertiesData,
    isLoading: isQueryLoading,
    isFetching: isQueryFetching,
    error: queryError,
  } = useGetWatchingPropertyQuery(undefined, { skip: !initiallyLogged });

  useEffect(() => {
    if (queryError) {
      console.error('Failed to fetch watched properties:', queryError);
      dispatch(setWatchedProperties([])); // Clears data and sets loading to false via reducer
    } else if (watchedPropertiesData) {
      dispatch(setWatchedProperties(watchedPropertiesData.properties || [])); // Sets data and loading to false
    } else {
      // Fallback: Not loading, no error, but no data. Ensure consistent state.
      dispatch(setWatchedProperties([]));
    }
  }, [watchedPropertiesData, isQueryLoading, isQueryFetching, queryError, dispatch]);

  const [getCurrentuser, { data: profile }] = useLazyGetCurrentUserQuery();
  const handleGoToProfile = useCallback(() => {
    navigate(`${PROFILE_ROUTE}?step=userProfile`);
  }, [navigate]);

  const goToDashboard = useCallback(() => {
    navigate(LANDING_ROUTE);
    dispatch(setInitialView(undefined));
  }, [navigate, dispatch]);

  const handleGoToAbout = useCallback(() => {
    navigate(ABOUT_ROUTE);
  }, [navigate]);

  const handleGoToFaq = useCallback(() => {
    navigate(FAQ_ROUTE);
  }, [navigate]);

  const handleGoToPricing = useCallback(() => {
    navigate(PRICING_ROUTE);
  }, [navigate]);

  const handleGoToLogin = useCallback(() => {
    navigate(DASHBOARD_ROUTE);
  }, [navigate]);

  const goToProperty = useCallback(() => {
    if (initiallyLogged) {
      dispatch(setActiveProperty(undefined));
      navigate(CREATE_PROPERTY_FORM_ROUTE);
    } else {
      notification.info({
        icon: <WarningOutlined style={{ color: '#8E53E8' }} />,
        message: 'You need to log in before listing a property.',
        placement: 'topRight',
      });
      navigate(DASHBOARD_ROUTE);
    }
  }, [navigate, notification, initiallyLogged, dispatch]);

  const goToServices = useCallback(() => {
    dispatch(setInitialView(undefined));
    navigate(MY_SERVICES_ROUTE);
  }, [navigate, dispatch]);

  const goToFavourites = useCallback(() => {
    if (initiallyLogged) {
      navigate('profile?step=favourites');
    } else {
      notification.info({
        icon: <WarningOutlined />,
        message: 'You need to log in.',
        placement: 'topRight',
      });
      navigate(DASHBOARD_ROUTE);
    }
  }, [navigate, notification, initiallyLogged]);

  useEffect(() => {
    if (initiallyLogged) {
      getCurrentuser();
    }
  }, [getCurrentuser, initiallyLogged]);

  useEffect(() => {
    if (profile) {
      dispatch(setProfile(profile));
    }
  }, [profile, dispatch]);

  // Fetch properties when the component mounts or when needed

  const handleGetDevList = useCallback(
    async (criteria) => {
      const res = await getDevelopmentList(criteria);
      if ('data' in res) {
        dispatch(setDevelopmentList(res.data.developments as Development[]));
      }
    },
    [dispatch, getDevelopmentList],
  );

  useEffect(() => {
    const criteria = {
      pageNumber: 1,
      pageSize: 200,
    };

    handleGetDevList(criteria);
  }, [handleGetDevList]);

  return (
    <div
      className={`${s.entireWrapper} ${serviceViewType === 'map' ? s.serviceMapNavbar : ''} ${
        location.pathname === '/' ? s.dashboardNavbar : ''
      }`}>
      <div className={s.navWrapper}>
        <Button style={{ paddingLeft: 0 }} type="link" onClick={goToDashboard}>
          <Icon width={200} name="logo" />
        </Button>
        <div className={s.menu}>
          <Button
            type="link"
            onClick={goToDashboard}
            className={`${s.menuItem} ${location.pathname === '/' ? s.active : ''}`}>
            Map
          </Button>
          {initiallyLogged && (
            <Button
              onClick={goToFavourites}
              style={{ color: '#8E53E8', display: 'flex', alignItems: 'center', gap: 4 }}
              type="link">
              <Typography type="h6" weight={700}>
                {watchedProperties?.length}
              </Typography>
              <Icon name="heartFilled" size={20} color="#8E53E8" />
            </Button>
          )}
          <Button
            type="link"
            onClick={goToServices}
            className={`${s.menuItem} ${location.pathname === '/services' ? s.active : ''}`}>
            Services
          </Button>
          <Button
            type="link"
            onClick={handleGoToAbout}
            className={`${s.menuItem} ${location.pathname === '/about' ? s.active : ''}`}>
            About
          </Button>
          <Button
            type="link"
            onClick={handleGoToFaq}
            className={`${s.menuItem} ${location.pathname === '/faq' ? s.active : ''}`}>
            FAQ
          </Button>
          <Button
            type="link"
            onClick={handleGoToPricing}
            className={`${s.menuItem} ${location.pathname === '/pricing' ? s.active : ''}`}>
            Pricing
          </Button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button className={s.post} onClick={goToProperty}>
            LIST PROPERTY
          </Button>
          {initiallyLogged ? (
            <Button onClick={handleGoToProfile} className={s.actions}>
              <div className={s.profile}>
                <div className={s.uploadedFile}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <img
                      className={s.uploadedImage}
                      src={resolveImageUrl(profile?.profileImageURL)}
                      width={24}
                      height={24}
                      alt="promotionMedia"
                    />
                  </div>
                </div>
              </div>
              <span className={s.text}>MY PROFILE</span>
            </Button>
          ) : (
            <Button onClick={handleGoToLogin} className={s.actions}>
              LOG IN
            </Button>
          )}
        </div>
      </div>
      <Filters />
    </div>
  );
};
export default NavBar;
